import { toast } from 'react-toastify';

import React, { useCallback, useRef, useState } from 'react';

import { useHistory } from 'react-router-dom';

import * as NavigationUtils from '../../utils/navigation';
import * as CanvasUtils from '../../utils/canvas';

import FormSignature from './FormSignature';
import Header from '../../components/Header';
import Container from '../../components/Container';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { usePatientFormContext } from '../../contexts/PatientForm';

function FormSignatureContainer() {
	const { innerWidth: width } = window;
	const containerRef = useRef()
	const textCanvasRef = useRef(null);

	const canvasOptions = useMemo(() => ({
		height: 212,
		width: width > 600 ? 440 : width - 34,
		className: 'signature-canvas'
	}), [width]);

	const { form, code, setInfo } = usePatientFormContext();

	const defaultSignatureOptions = {
		signature_type: form?.signature_type || 'TEXT',
		signature_text: form?.signature_text || form?.patient?.name || ''
	};

	const history = useHistory();
	const [signatureCanvas, setSignatureCanvas] = useState();
	const [loading, setLoading] = useState(true);
	const [signatureOptions, setSignatureOptions] = useState(defaultSignatureOptions);
	const getCanvasRef = useCallback(ref => setSignatureCanvas(ref), []);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [containerRef])

	useEffect(() => {
		setTimeout(() => {
			setLoading(false);
		}, 400);
	}, [loading]);

	useEffect(() => {
		if (form?.signature_url && signatureOptions.signature_type === 'CANVAS') {
			signatureCanvas?.fromDataURL(form.signature_url, canvasOptions);
		}

		if (!form || !code) {
			return NavigationUtils.navigate(history, `?code=${code}`);
		}
	}, [signatureCanvas, canvasOptions, history, code, form, form?.signature_url, signatureOptions]);

	const saveText = useCallback( canvas => {
		const context = canvas.getContext('2d');
		CanvasUtils.mountCanvasText({
			text: signatureOptions.signature_text,
			canvas: context,
			width: canvas.width,
			lineHeight: 42,
		})

		const trimmedUrl = canvas?.toDataURL('image/png');

		setInfo(values => ({
			...values,
			form: {
				...values.form,
				signature_url: trimmedUrl,
				signature_text: signatureOptions.signature_text,
				signature_type: signatureOptions.signature_type
			}
		}));
	}, [signatureOptions, setInfo]);

	const saveCanvas = useCallback( canvas => {
		const trimmedUrl = canvas.toDataURL('image/png');

		setInfo(values => ({
			...values,
			form: {
				...values.form,
				signature_url: trimmedUrl,
				signature_type: signatureOptions.signature_type
			}
		}));
	}, [signatureOptions.signature_type, setInfo]);

	const save = useCallback(() => {
		const isTextSignature = signatureOptions.signature_type === 'TEXT';

		if ((signatureCanvas.isEmpty() && !isTextSignature) || (isTextSignature && !signatureOptions.signature_text.length)) {
			return toast.warn('É necessário assinar o documento para salvar');
		}

		if (isTextSignature) {
			saveText(textCanvasRef.current);
		} else {
			saveCanvas(signatureCanvas);
		}

		return NavigationUtils.navigate(history, `/forms/${code}?cameFromSignature=true`);
	}, [signatureCanvas, history, code, signatureOptions, saveCanvas, saveText]);

	const onChangeSignatureType = useCallback( type => {
		delete form.signature_url;

		setSignatureOptions(currentOptions => ({
			signature_type: type,
			signature_text: currentOptions.signature_text
		}));
	}, [form.signature_url]);

	const onChangeSignatureText = useCallback((event) => {
		return setSignatureOptions(currentOptions => ({
			signature_type: currentOptions.signature_type,
			signature_text: event.target.value
		}));
	}, []);

	const handleKeyDown = useCallback(event => {
		if (event.key === 'Enter') {
			event.preventDefault();
			return;
		}

		onChangeSignatureText(event);
	}, [onChangeSignatureText]);

	const clearCanvas = useCallback(() => {
		return signatureCanvas?.clear();
	}, [signatureCanvas]);

	const clearText = useCallback(() => {
		setSignatureOptions(currentOptions => ({
			signature_type: currentOptions.signature_type,
			signature_text: ''
		}))
	}, []);

	const clear = useCallback(() => {
		if (signatureOptions.signature_type === 'CANVAS') {
			return clearCanvas();
		} else if (signatureOptions.signature_type === 'TEXT') {
			return clearText();
		}
	}, [signatureOptions, clearCanvas, clearText]);

	const undo = useCallback(() => {
		if (signatureCanvas.isEmpty()) {
			return;
		}

		const newCanvas = signatureCanvas.toData();

		newCanvas.pop();

		signatureCanvas.fromData(newCanvas);
	}, [signatureCanvas]);

    return (
		<Container >
			<Header/>

			<FormSignature
				canvasOptions={canvasOptions}
				save={save}
				clear={clear}
				undo={undo}
				loading={loading}
				getCanvasRef={getCanvasRef}
				textCanvasRef={textCanvasRef}
				signatureOptions={signatureOptions}
				onChangeSignatureType={onChangeSignatureType}
				onChangeSignatureText={onChangeSignatureText}
				handleKeyDown={handleKeyDown}
			/>
		</Container>
	);
}

export default FormSignatureContainer;
